// apps/web/src/components/rights/RightsDashboard.tsx
// Rights management dashboard

import type React from 'react';
import { useState, useEffect } from 'react';
import type { Rights } from '../../types/rights';
import { apiClient } from '../../lib/apiClient';

export function RightsDashboard() {
  const [rights, setRights] = useState<Rights[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showGrantForm, setShowGrantForm] = useState(false);

  useEffect(() => {
    const fetchRights = async () => {
      try {
        setLoading(true);
        const response = await apiClient.get('/api/v1/rights');
        setRights(response.data.rights);
      } catch (err) {
        setError('Failed to load rights');
        console.error('Error loading rights:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchRights();
  }, []);

  const handleGrantRights = async (rightsData: any) => {
    try {
      const response = await apiClient.post('/api/v1/rights', rightsData);
      setRights([response.data, ...rights]);
      setShowGrantForm(false);
    } catch (err) {
      setError('Failed to grant rights');
      console.error('Error granting rights:', err);
    }
  };

  if (loading) {
    return <div className="loading">Loading rights...</div>;
  }

  if (error) {
    return <div className="error">{error}</div>;
  }

  return (
    <div className="rights-dashboard">
      <div className="header">
        <h2>Rights Management</h2>
        <button 
          onClick={() => setShowGrantForm(!showGrantForm)}
          className="btn btn-primary"
        >
          {showGrantForm ? 'Cancel' : 'Grant Rights'}
        </button>
      </div>

      {showGrantForm && (
        <RightsGrantForm 
          onSubmit={handleGrantRights} 
          onCancel={() => setShowGrantForm(false)} 
        />
      )}

      <div className="rights-grid">
        {rights.map(right => (
          <RightsCard key={right.id} rights={right} />
        ))}
      </div>
    </div>
  );
}

// Helper component
function RightsCard({ rights }: { rights: Rights }) {
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'ACTIVE': return 'status-active';
      case 'EXPIRED': return 'status-expired';
      case 'REVOKED': return 'status-revoked';
      default: return 'status-default';
    }
  };

  return (
    <div className="rights-card">
      <div className="rights-header">
        <h3>{rights.rightsType} Rights</h3>
        <span className={`status-badge ${getStatusColor(rights.status)}`}>
          {rights.status}
        </span>
      </div>
      <p><strong>Content:</strong> {rights.contentAssetId.substring(0, 8)}...</p>
      <p><strong>Creator:</strong> {rights.creatorId.substring(0, 8)}...</p>
      <p><strong>Grantor:</strong> {rights.grantorId.substring(0, 8)}...</p>
      <p><strong>Period:</strong> {rights.startDate} to {rights.endDate}</p>
      <p><strong>Exclusivity:</strong> {rights.exclusivity ? 'Yes' : 'No'}</p>
      <p><strong>Rate:</strong> {rights.royaltyRate}%</p>
      <div className="actions">
        <button className="btn btn-secondary">View Details</button>
        <button className="btn btn-warning">Edit</button>
        {rights.status === 'ACTIVE' && (
          <button className="btn btn-danger">Revoke</button>
        )}
      </div>
    </div>
  );
}

// Helper component
function RightsGrantForm({ onSubmit, onCancel }: { onSubmit: any; onCancel: any }) {
  const [formData, setFormData] = useState({
    contentAssetId: '',
    creatorId: '',
    grantorId: '',
    startDate: '',
    endDate: '',
    rightsType: 'USAGE',
    exclusivity: false,
    royaltyRate: 0,
    licenseTerms: ''
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit(formData);
  };

  return (
    <form onSubmit={handleSubmit} className="rights-grant-form">
      <h3>Grant New Rights</h3>
      <input
        type="text"
        placeholder="Content Asset ID"
        value={formData.contentAssetId}
        onChange={(e) => setFormData({...formData, contentAssetId: e.target.value})}
        required
      />
      <input
        type="text"
        placeholder="Creator ID"
        value={formData.creatorId}
        onChange={(e) => setFormData({...formData, creatorId: e.target.value})}
        required
      />
      <input
        type="text"
        placeholder="Grantor ID"
        value={formData.grantorId}
        onChange={(e) => setFormData({...formData, grantorId: e.target.value})}
        required
      />
      <input
        type="date"
        value={formData.startDate}
        onChange={(e) => setFormData({...formData, startDate: e.target.value})}
      />
      <input
        type="date"
        value={formData.endDate}
        onChange={(e) => setFormData({...formData, endDate: e.target.value})}
      />
      <select
        value={formData.rightsType}
        onChange={(e) => setFormData({...formData, rightsType: e.target.value})}
      >
        <option value="USAGE">Usage</option>
        <option value="REPRODUCTION">Reproduction</option>
        <option value="DERIVATIVE">Derivative</option>
        <option value="PUBLICATION">Publication</option>
        <option value="OTHER">Other</option>
      </select>
      <label>
        <input
          type="checkbox"
          checked={formData.exclusivity}
          onChange={(e) => setFormData({...formData, exclusivity: e.target.checked})}
        />
        Exclusive Rights
      </label>
      <input
        type="number"
        placeholder="Royalty Rate (%)"
        value={formData.royaltyRate}
        onChange={(e) => setFormData({...formData, royaltyRate: parseInt(e.target.value) || 0})}
        min="0"
        max="100"
      />
      <textarea
        placeholder="License Terms"
        value={formData.licenseTerms}
        onChange={(e) => setFormData({...formData, licenseTerms: e.target.value})}
      />
      <div className="form-actions">
        <button type="submit" className="btn btn-primary">Grant Rights</button>
        <button type="button" onClick={onCancel} className="btn btn-secondary">Cancel</button>
      </div>
    </form>
  );
}